import Swal from "sweetalert2";
import axiosInstance from "../../axiosInstance";

// Same Trustpilot verified review collector the student portal shows after payment.
const BUSINESS_UNIT_ID =
  process.env.REACT_APP_TRUSTPILOT_BUSINESS_UNIT_ID || "57dda5e00000ff000594d5e6";
const TEMPLATE_ID = "53ch7c1d9be8a02f64d31e79";
const SCRIPT_SRC =
  "https://reviewcollection.trustpilot.com/verifiedreviewcollector/main.js";
const REVIEW_URL = "https://www.trustpilot.com/review/londonist.co.uk";
const MOUNT_ID = "trustpilot-partner-review-mount";

let scriptPromise = null;

const loadCollectorScript = () => {
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error("Trustpilot script failed to load"));
      };
      document.head.appendChild(script);
    }).then(() =>
      customElements.whenDefined("trustbox-verified-review-collector")
    );
  }
  return scriptPromise;
};

const shouldBypassHostCheck = () =>
  process.env.REACT_APP_TRUSTPILOT_HOST_BYPASS === "true" ||
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
  XRefreshToken: `${localStorage.getItem("loginUser")}`,
  "Content-Type": "application/json",
});

const reviewUrl = `${process.env.REACT_APP_BASE_URL}auth/trustpilot/review`;

const recordReview = (action) =>
  axiosInstance
    .post(reviewUrl, { action }, { headers: authHeaders() })
    .catch((error) => console.error("Trustpilot review state not saved", error));

const mountCollector = async (payload) => {
  const host = document.getElementById(MOUNT_ID);
  if (!host) return;
  try {
    await loadCollectorScript();
  } catch {
    host.innerHTML = `<a href="${REVIEW_URL}" target="_blank" rel="noopener noreferrer">Leave a review on Trustpilot</a>`;
    return;
  }
  if (!document.getElementById(MOUNT_ID)) return; // popup closed meanwhile

  const collector = document.createElement("trustbox-verified-review-collector");
  collector.className = "trustpilot-widget";
  collector.setAttribute("style-size", "L");
  collector.setAttribute("locale", "en-US");
  collector.setAttribute("template-id", TEMPLATE_ID);
  collector.setAttribute("businessunit-id", BUSINESS_UNIT_ID);
  collector.setAttribute("style-width", "100%");
  collector.setAttribute("border-type", "shadow");
  collector.setAttribute("background", "light");
  collector.setAttribute("has-animated-stars", "true");
  collector.setAttribute("tags", "PartnerPortal");
  if (shouldBypassHostCheck()) collector.setAttribute("host-bypass", "true");
  collector.onReady = () =>
    collector.dispatchEvent(
      new CustomEvent("customerDataResponse", { detail: payload, bubbles: true })
    );
  host.replaceChildren(collector);
};

const showTrustpilotPrompt = (title, payload) => {
  recordReview("shown");
  Swal.fire({
    icon: "success",
    title,
    html: `
      <p style="margin:0 0 12px">How is your partnership with Londonist going? We'd really value a quick review of our services.</p>
      <div id="${MOUNT_ID}" style="min-height:120px"></div>
    `,
    width: 640,
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: "I've left my review",
    cancelButtonText: "Remind me later",
    allowOutsideClick: false,
    customClass: { popup: "trustpilot-review-popup" },
    didOpen: () => mountCollector(payload),
  }).then((result) => {
    if (result.isConfirmed) recordReview("reviewed");
  });
};

/**
 * Success alert after an agent shares a quote with a student. Once per agent it
 * also asks for a Trustpilot review of the partnership; until the agent confirms
 * it, the ask comes back every few days (the backend decides when — see
 * custom_auth/trustpilot.py). With `fallback: false` nothing is shown when no
 * review is due.
 */
export default async function showSuccessWithTrustpilot(title, { fallback = true } = {}) {
  try {
    const { data } = await axiosInstance.get(reviewUrl, { headers: authHeaders() });
    if (data?.show && data?.payload) {
      showTrustpilotPrompt(title, data.payload);
      return;
    }
  } catch (error) {
    console.error("Trustpilot review check failed", error);
  }
  if (!fallback) return;
  Swal.fire({
    icon: "success",
    title,
    showConfirmButton: false,
    showCloseButton: true,
  });
}
